import { useSourcingInternalStore } from 'modules/sourcing-internal/stores/hooks';
import { SourcingSharedReviewInvitationsStep } from 'modules/sourcing-shared/shared-steps';
import { getSourcingInternalHeaderBreadcrumbs } from 'modules/sourcing-internal/utils';
import { useShallow } from 'zustand/react/shallow';
import {
  useUpdateSearchCandidateProfileSelectedItemMutation,
  useGenerateCandidateProfileRelevanceMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useCallback } from 'react';
import { SourcingSharedCandidateBatch } from 'modules/sourcing-shared/shared-stores/types';
import {
  SourcingSharedCustomizeMessageEmailFormData,
  SourcingSharedCustomizeMessageSmsFormData
} from 'modules/sourcing-shared/forms';
import { Channels } from 'generated-graphql/types';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

export const SourcingInternalReviewInvitationsStep = () => {
  const {
    sessionId,
    selectedCustomizeCandidateId,
    candidateBatch,
    channelFormData,
    applicationFormData,
    jobInformationFormData,
    customizeMessageFormData,
    selectedCandidateProfiles,
    editedOpeningMessage,
    setSelectedCustomizeCandidateId,
    onReviewInvitationsBack,
    onInviteSubmit,
    updateSelectedRowCustomMessage,
    setStep
  } = useSourcingInternalStore(
    useShallow((state) => ({
      sessionId: state.sessionId,
      selectedCandidateProfiles: state.selectedCandidateProfiles,
      selectedCustomizeCandidateId: state.selectedCustomizeCandidateId,
      candidateBatch: state.candidateBatch,
      channelFormData: state.channelFormData,
      applicationFormData: state.applicationFormData,
      jobInformationFormData: state.jobInformationFormData,
      customizeMessageFormData: state.customizeMessageFormData,
      editedOpeningMessage: state.editedOpeningMessage,
      setSelectedCustomizeCandidateId: state.setSelectedCustomizeCandidateId,
      onReviewInvitationsBack: state.onReviewInvitationsBack,
      onInviteSubmit: state.onInviteSubmit,
      updateSelectedRowCustomMessage: state.updateSelectedRowCustomMessage,
      setStep: state.setStep
    }))
  );

  const [updateSearchCandidateProfileSelectedItem] =
    useUpdateSearchCandidateProfileSelectedItemMutation();
  const [
    generateCandidateProfileRelevance,
    { loading: isGeneratingRelevance }
  ] = useGenerateCandidateProfileRelevanceMutation();

  const handleUpdateInvitationMessage = useCallback(
    async (candidateId: string, customMessage: string) => {
      await updateSearchCandidateProfileSelectedItem({
        variables: {
          input: {
            id: candidateId,
            customInvitationMessage: customMessage
          }
        }
      });

      updateSelectedRowCustomMessage(candidateId, customMessage);
    },
    [updateSearchCandidateProfileSelectedItem, updateSelectedRowCustomMessage]
  );

  const handleGenerateRelevance = useCallback(
    async (
      searchCandidateProfileSelectedItemId: string,
      candidateBatch: SourcingSharedCandidateBatch,
      customizeMessageFormData:
        | SourcingSharedCustomizeMessageEmailFormData
        | SourcingSharedCustomizeMessageSmsFormData
    ): Promise<string> => {
      try {
        const { data } = await generateCandidateProfileRelevance({
          variables: {
            input: {
              searchCandidateProfileSelectedItemId:
                searchCandidateProfileSelectedItemId,
              customTemplatedMessage:
                customizeMessageFormData?.customTemplatedMessage ?? '',
              employerName:
                candidateBatch?.jobDescriptionParsed?.employerName || undefined,
              jobDescriptionText: candidateBatch?.jobDescription || '',
              jobTitle: candidateBatch?.jobDescriptionParsed?.jobTitle ?? '',
              channel: channelFormData?.channel
            }
          }
        });

        return (
          data?.generateCandidateProfileRelevance?.candidateRelevance || ''
        );
      } catch (error) {
        // Prevent infinite loop
        return '';
      }
    },
    []
  );

  // Wrapper to convert SearchCandidateProfileSelectedItem ID to candidateProfile ID
  const handleSetSelectedCandidateId = useCallback(
    (searchCandidateProfileSelectedItemId: string) => {
      const profile = selectedCandidateProfiles?.find(
        (p) => p.id === searchCandidateProfileSelectedItemId
      );
      if (profile?.candidateProfile?.id) {
        setSelectedCustomizeCandidateId(profile.candidateProfile.id);
      }
    },
    [selectedCandidateProfiles, setSelectedCustomizeCandidateId]
  );

  if (
    !candidateBatch ||
    !customizeMessageFormData ||
    !jobInformationFormData ||
    !applicationFormData ||
    !selectedCandidateProfiles
  )
    return null;

  return (
    <SourcingSharedReviewInvitationsStep
      channel={channelFormData?.channel ?? Channels.Email}
      headerBreadcrumbs={getSourcingInternalHeaderBreadcrumbs({
        candidateBatchJobTitle: getAnalysisTitle(candidateBatch),
        candidateBatchId: candidateBatch?.id ?? '',
        setStep
      })}
      candidateItems={selectedCandidateProfiles.map((item) => {
        const candidateProfile = item.candidateProfile;
        const fullName = `${candidateProfile?.firstName ?? ''} ${
          candidateProfile?.lastName ?? ''
        }`.trim();
        const location = candidateProfile?.location?.name ?? '';
        const foundTitle =
          candidateProfile?.jobs?.find((job) => job.currentRole)?.jobTitle ??
          '';

        // Build experience array from jobs with descriptions
        const experience =
          candidateProfile?.jobs
            ?.filter((job) => job.description)
            .map((job) => job.description as string) ?? [];

        // Build education array
        const education =
          candidateProfile?.education
            ?.map((edu) => {
              const parts = [edu.course, edu.institution, edu.grade].filter(
                Boolean
              );
              return parts.join(' • ');
            })
            .filter((str) => str.length > 0) ?? [];

        return {
          id: item.id ?? '',
          fullName,
          location,
          title: foundTitle,
          customInvitationMessage: item.customInvitationMessage ?? '',
          profile: {
            id: candidateProfile?.id ?? '',
            fullName,
            linkedInProfileUrl: candidateProfile?.linkedInProfileUrl ?? '',
            location,
            title: foundTitle,
            source: 'INTERNAL' as const,
            skills:
              candidateProfile?.skills?.filter((skill) => skill !== null) ?? [],
            education,
            experience
          }
        };
      })}
      selectedCandidateId={
        // If a candidateProfile ID is selected, find the corresponding SearchCandidateProfileSelectedItem ID
        selectedCustomizeCandidateId
          ? selectedCandidateProfiles.find(
              (profile) =>
                profile.candidateProfile?.id === selectedCustomizeCandidateId
            )?.id ?? ''
          : selectedCandidateProfiles[0]?.id ?? ''
      }
      candidateBatch={candidateBatch}
      customizeMessageFormData={customizeMessageFormData}
      jobInformationFormData={jobInformationFormData}
      applicationFormData={applicationFormData}
      editedOpeningMessage={editedOpeningMessage}
      setSelectedCandidateId={handleSetSelectedCandidateId}
      onReviewInvitationsBack={onReviewInvitationsBack}
      onInviteSubmit={async () =>
        await onInviteSubmit({
          // Internal sourcing uses searchCandidateProfileSelectedId (sessionId)
          type: 'internal',
          searchCandidateProfileSelectedId: sessionId ?? ''
        })
      }
      onUpdateInvitationMessage={handleUpdateInvitationMessage}
      onGenerateRelevance={handleGenerateRelevance}
      isGeneratingRelevance={isGeneratingRelevance ?? false}
    />
  );
};
